"use client"

import { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { apiClient, type SubscriptionPlan } from "@/lib/api-client"

export function SubscriptionPlans() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPlans()
  }, [])

  const loadPlans = async () => {
    try {
      setLoading(true)
      setError(null)
      const allPlans = await apiClient.getAllPlans()
      setPlans(allPlans)
    } catch (err: any) {
      console.error("Error loading plans:", err)
      setError(err.message || "Failed to load subscription plans")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-[#13161C] rounded-3xl p-12 border border-white/5 shadow-2xl">
        <div className="flex items-center justify-center space-x-3">
          <Loader2 className="h-6 w-6 animate-spin text-[#25e4e8]" />
          <p className="text-[#8B949E] text-lg">Loading subscription plans...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-[#13161C] rounded-3xl p-6 border border-white/5 shadow-2xl animate-slide-up">
        <div className="text-center">
          <p className="text-red-400 mb-4 text-lg">{error}</p>
          <button
            onClick={loadPlans}
            className="px-6 py-2 rounded-xl bg-[#25e4e8] text-black font-bold hover:bg-[#1dd1d5] transition"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (plans.length === 0) {
    return (
      <div className="bg-[#13161C] rounded-3xl p-12 border border-white/5 shadow-2xl animate-slide-up">
        <div className="text-center">
          <p className="text-[#8B949E] text-lg">No subscription plans available</p>
          <p className="text-sm text-[#8B949E] mt-2">Plans will appear here once they are created</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex justify-between items-center">
        <div className="text-lg font-bold text-white">Available Subscription Plans</div>
        <button
          onClick={loadPlans}
          className="px-4 py-2 rounded-xl bg-[#1C1F26] border border-white/5 text-[#8B949E] hover:text-white text-sm font-medium transition"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.map((plan, idx) => {
          // IRMA has 9 decimals - ensure consistent conversion
          const price = parseFloat(plan.priceInIrma) / 1_000_000_000
          const affiliateCredit = price * 0.1

          return (
            <div
              key={plan.platformId}
              className="bg-[#13161C] rounded-3xl p-5 border border-white/5 shadow-2xl hover-lift animate-slide-up"
              style={{ animationDelay: `${idx * 0.03}s` }}
            >
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold text-white">{plan.planName}</h3>
                    <p className="text-xs text-[#8B949E] mt-1">{plan.platformId}</p>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                    Active
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B949E]">Price</span>
                      <span className="text-xl font-bold text-[#25e4e8]">{price.toFixed(4)} IRMA</span>
                    </div>
                  </div>

                  <div className="bg-[#1C1F26] rounded-2xl p-3 border border-transparent">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B949E]">Duration</span>
                      <span className="text-sm font-semibold text-white">{plan.durationDays} days</span>
                    </div>
                  </div>

                  <div className="bg-[#1C1F26] rounded-2xl p-3 border border-green-500/20">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#8B949E]">Affiliate Credit</span>
                      <span className="text-sm font-bold text-green-400">
                        {affiliateCredit.toFixed(2)} IRMA (10%)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-white/5">
                  <p className="text-xs text-[#8B949E]">
                    💰 Earn {affiliateCredit.toFixed(2)} IRMA for each referral
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
